import { useEffect, useState } from "react";
import { Download } from "lucide-react";

/**
 * PwaInstallButton — surfaces a one-tap "Install App" pill in the navbar
 * when the browser fires `beforeinstallprompt`. Tapping triggers the
 * native install flow (Chrome/Edge desktop, Android Chrome, Samsung
 * Internet). Hides automatically when:
 *   - the app is already installed (display-mode: standalone)
 *   - the browser doesn't support the prompt (Safari iOS, older Firefox)
 *   - the user has already accepted/dismissed the prompt this session
 *
 * iOS Safari has no `beforeinstallprompt`; we rely on users using the
 * browser share sheet → "Add to Home Screen" there. The home-screen
 * shortcut is honoured by our manifest + apple-touch-icon meta tags.
 */
export default function PwaInstallButton({ className = "", testId = "pwa-install-btn" }) {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Already-installed standalone window — no need to advertise install.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true;
    if (standalone) {
      setHidden(true);
      return undefined;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setHidden(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden || !deferred) return null;

  const onClick = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      // Outcome is "accepted" or "dismissed". Either way the prompt is
      // a one-shot — clear it so the button vanishes.
      setDeferred(null);
      if (choice?.outcome === "dismissed") {
        // Don't pester — the prompt won't re-fire this session anyway.
        setHidden(true);
      }
    } catch {
      setDeferred(null);
    }
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#ff007f]/60 bg-[#ff007f]/10 hover:bg-[#ff007f]/25 hover:border-[#ff007f] transition font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-[#ff007f] ${className}`}
      data-testid={testId}
      title="Install Cosmic Worm Breaker as an app"
    >
      <Download className="w-3 h-3" />
      Install App
    </button>
  );
}
